import React, { useEffect } from 'react';
import { socialDatasetId } from "system/query/Query";
import { Box, Pagination, Typography } from '@mui/material';
import DatasetRow from './DatasetRow';
import { FetchWrapper } from 'components/apiHandling/FetchWrapper';
import APIRequest, { APIError } from 'system/APIRequest';
import { Dataset } from './Dataset';
import { permissionTest } from "system/InfegyUser";
import { enqueueSnackbar } from 'notistack';

export interface DatasetListProps {
  selectedDataset: Dataset | undefined;
  setSelectedDataset: (datasets: Dataset | undefined) => void;

  // Show Datasets shared by other users
  viewShared?: boolean;

  // Search String compared against Dataset Name
  searchString?: string;

  // Search field, most likely "title" or "description"
  searchField?: string;
}

export default function DatasetList(props: DatasetListProps): JSX.Element {
  const [DatasetListResponse, setDatasetListResponse] = React.useState<any>(undefined);
  const [datasetsAreLoading, setDatasetsAreLoading] = React.useState<boolean>(true);
  const [datasetsError, setQueryError] = React.useState<any>(undefined);

  const [socialDataset, setSocialDataset] = React.useState<Dataset | undefined>(undefined);

  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const totalPages = Math.ceil(DatasetListResponse?.pagination?.total / DatasetListResponse?.pagination?.per_page) || 0;

  const hasSocialData = permissionTest("social_data");

  useEffect(() => {
    if (hasSocialData) {
      APIRequest(`obj/dataset/${socialDatasetId}`).then((response) => {
        setSocialDataset(response.output);
      }).catch((apiError:APIError) => {
        enqueueSnackbar(`Failed to load social dataset. ${apiError.message} (${apiError.code} ${apiError.server_message}`, {variant: "error"});
      });
    }
  }, [hasSocialData]);

  useEffect(() => {
    const searchField = props.searchField || "name";

    const requestOptions:any = {
      "per_page":10,
      "page":currentPage,
      "sort":"updated_at",
      "sort_order":"desc"
    };
    if (props.viewShared) {
      requestOptions["include_shared"] = 1;
    }
    if (props.searchString) {
      const searchObj = [{"id":searchField,"op":"contains","value":props.searchString}];
      requestOptions["q"] = JSON.stringify(searchObj);
    }

    APIRequest("obj/dataset", requestOptions).then((response) => {
      setDatasetListResponse(response);
      setDatasetsAreLoading(false);
      setQueryError(undefined);
    }).catch((error) => {
      setQueryError(error);
      setDatasetListResponse(undefined);
      setDatasetsAreLoading(false);
    });
  }, [props.searchString, props.viewShared, props.searchField, currentPage]);

  const outputRows = DatasetListResponse?.output?.
    filter((dataset: Dataset) => {
      // Filter out the social dataset unless the user is searching for it,
      // since the social dataset is always shown at the top of the list.
      return props.searchString || dataset.id !== socialDatasetId;
    }).map((dataset: Dataset, index: number) => {
    return (
      <DatasetRow
        key={index}
        isSocialData={dataset.id === socialDatasetId}
        dataset={dataset}
        onClick={() => {
          props.setSelectedDataset(dataset)
        }}
        selectedDatasetId={props.selectedDataset?.id}
      />
    );
  });

  return (
    <Box>
      <FetchWrapper isLoading={datasetsAreLoading} fetchError={datasetsError} >
        {/* Show the social dataset at the top of the first page of results
          if the user has access and is not actively searching */}
        {hasSocialData && socialDataset && !props.searchString && currentPage === 1 &&
          <>
          <Typography variant="h4" sx={{ mb: 1 }} >Social Dataset:</Typography>
          <DatasetRow
            dataset={socialDataset}
            isSocialData={true}
            onClick={() => {
              if (props.selectedDataset?.id === socialDatasetId) {
                props.setSelectedDataset(undefined);
              } else {
                props.setSelectedDataset(socialDataset)
              }
            }}
            selectedDatasetId={props.selectedDataset?.id}
          />
          <Typography variant="h4" sx={{ mb: 1 }} >Others:</Typography>
          </>
        }
        {outputRows}
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={(event: React.ChangeEvent<unknown>, value: number) => {
            setCurrentPage(value);
          }}
          shape="rounded"
          sx={{display:'flex', justifyContent:'center'}}
        />
      </FetchWrapper>
    </Box>
  );
}
